import { randomUUID } from 'node:crypto';
import type { CatalogSnapshot } from '@eventcatalog/sdk';
import type { CompatibilityStrategy } from '@eventcatalog/breaking-changes';
import type { GovernanceResult } from './types';
import { resolveEnvVars, isProducerTrigger, getChangeVerb } from './rules';

export type MessageTypeMap = Map<string, string>;
export type ServiceOwnersMap = Map<string, string[]>;

export type GovernanceActionOptions = {
  messageTypes?: MessageTypeMap;
  status?: string;
  serviceOwners?: ServiceOwnersMap;
  baseRef?: string;
  targetRef?: string;
  compatibilityStrategy?: CompatibilityStrategy;
};

export const buildMessageTypeMap = (snapshot: CatalogSnapshot): MessageTypeMap => {
  const map: MessageTypeMap = new Map();
  for (const event of snapshot.resources.messages.events) {
    map.set(event.id as string, 'event');
  }
  for (const command of snapshot.resources.messages.commands) {
    map.set(command.id as string, 'command');
  }
  for (const query of snapshot.resources.messages.queries) {
    map.set(query.id as string, 'query');
  }
  return map;
};

export const buildServiceOwnersMap = (snapshot: CatalogSnapshot): ServiceOwnersMap => {
  const map: ServiceOwnersMap = new Map();
  for (const service of snapshot.resources.services) {
    if (service.owners && Array.isArray(service.owners) && service.owners.length > 0) {
      map.set(service.id as string, service.owners as string[]);
    }
  }
  return map;
};

export const executeGovernanceActions = async (
  results: GovernanceResult[],
  opts: GovernanceActionOptions = {}
): Promise<string[]> => {
  const { messageTypes, status, serviceOwners, baseRef, targetRef, compatibilityStrategy } = opts;
  const webhookCalls: Array<{ urlTemplate: string; request: Promise<Response> }> = [];
  const now = new Date().toISOString();

  for (const result of results) {
    for (const action of result.rule.actions) {
      if (action.type !== 'webhook') continue;

      const url = resolveEnvVars(action.url);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      if (action.headers) {
        for (const [key, value] of Object.entries(action.headers)) {
          headers[key] = resolveEnvVars(value);
        }
      }

      // Handle schema changes
      if (result.schemaChanges && result.schemaChanges.length > 0) {
        for (const sc of result.schemaChanges) {
          const messageType = messageTypes?.get(sc.resourceChange.resourceId) || 'message';

          const payload = {
            specversion: '1.0',
            type: 'eventcatalog.governance.schema_changed',
            source: 'eventcatalog/governance',
            id: randomUUID(),
            time: now,
            datacontenttype: 'application/json',
            data: {
              schemaVersion: 1,
              ...(status && { status }),
              summary: `Schema changed for ${messageType} ${sc.resourceChange.resourceId}`,
              message: {
                id: sc.resourceChange.resourceId,
                version: sc.resourceChange.version,
                type: messageType,
              },
              schema: {
                beforeHash: sc.beforeSchemaHash ?? null,
                afterHash: sc.afterSchemaHash ?? null,
                beforePath: sc.beforeSchemaPath ?? null,
                afterPath: sc.afterSchemaPath ?? null,
              },
              refs: {
                base: baseRef ?? null,
                target: targetRef ?? null,
              },
              consumers: sc.consumerServices,
              producers: sc.producerServices,
            },
          };

          webhookCalls.push({
            urlTemplate: action.url,
            request: fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }),
          });
        }
        continue;
      }

      // Handle breaking schema changes
      if (result.breakingSchemaChanges && result.breakingSchemaChanges.length > 0) {
        for (const bsc of result.breakingSchemaChanges) {
          const messageType = messageTypes?.get(bsc.resourceChange.resourceId) || 'message';

          const payload = {
            specversion: '1.0',
            type: 'eventcatalog.governance.schema_breaking_change',
            source: 'eventcatalog/governance',
            id: randomUUID(),
            time: now,
            datacontenttype: 'application/json',
            data: {
              schemaVersion: 1,
              ...(status && { status }),
              ...(compatibilityStrategy && { compatibilityStrategy }),
              summary: `Breaking schema change detected for ${messageType} ${bsc.resourceChange.resourceId}`,
              message: {
                id: bsc.resourceChange.resourceId,
                version: bsc.resourceChange.version,
                type: messageType,
              },
              schema: {
                beforeHash: bsc.beforeSchemaHash ?? null,
                afterHash: bsc.afterSchemaHash ?? null,
                beforePath: bsc.beforeSchemaPath ?? null,
                afterPath: bsc.afterSchemaPath ?? null,
              },
              breakingChanges: bsc.breakingChanges,
              refs: {
                base: baseRef ?? null,
                target: targetRef ?? null,
              },
              consumers: bsc.consumerServices,
              producers: bsc.producerServices,
            },
          };

          webhookCalls.push({
            urlTemplate: action.url,
            request: fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }),
          });
        }
        continue;
      }

      // Handle deprecation changes
      if (result.deprecationChanges && result.deprecationChanges.length > 0) {
        for (const dc of result.deprecationChanges) {
          const messageType = messageTypes?.get(dc.resourceChange.resourceId) || 'message';

          const producers =
            dc.producerServices.length > 0
              ? dc.producerServices
              : [{ id: 'unknown', version: 'unknown' } as { id: string; version: string; owners?: string[] }];

          for (const producer of producers) {
            const payload = {
              specversion: '1.0',
              type: `eventcatalog.governance.message_deprecated`,
              source: 'eventcatalog/governance',
              id: randomUUID(),
              time: now,
              datacontenttype: 'application/json',
              data: {
                schemaVersion: 1,
                ...(status && { status }),
                summary: `${dc.resourceChange.resourceId} (${messageType}) has been deprecated by ${producer.id}`,
                producer: {
                  id: producer.id,
                  version: producer.version,
                  ...(producer.owners && { owners: producer.owners }),
                },
                message: {
                  id: dc.resourceChange.resourceId,
                  version: dc.resourceChange.version,
                  type: messageType,
                },
              },
            };

            webhookCalls.push({
              urlTemplate: action.url,
              request: fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }),
            });
          }
        }
        continue;
      }

      // Handle relationship changes
      for (const change of result.matchedChanges) {
        const verb = getChangeVerb(result.trigger, change.changeType);
        const messageType = messageTypes?.get(change.resourceId) || 'message';
        const serviceRole = isProducerTrigger(result.trigger) ? 'producer' : 'consumer';

        const payload = {
          specversion: '1.0',
          type: `eventcatalog.governance.${result.trigger}`,
          source: 'eventcatalog/governance',
          id: randomUUID(),
          time: now,
          datacontenttype: 'application/json',
          data: {
            schemaVersion: 1,
            ...(status && { status }),
            summary: `${change.serviceId} is ${verb} the ${messageType} ${change.resourceId}`,
            [serviceRole]: {
              id: change.serviceId,
              version: change.serviceVersion,
              ...(serviceOwners?.get(change.serviceId) && { owners: serviceOwners.get(change.serviceId) }),
            },
            message: {
              id: change.resourceId,
              version: change.resourceVersion,
              type: messageType,
            },
          },
        };

        webhookCalls.push({
          urlTemplate: action.url,
          request: fetch(url, { method: 'POST', headers, body: JSON.stringify(payload) }),
        });
      }
    }
  }

  const settled = await Promise.allSettled(webhookCalls.map((c) => c.request));

  return settled.map((result, i) => {
    const url = webhookCalls[i].urlTemplate;
    if (result.status === 'fulfilled') {
      const res = result.value;
      if (!res.ok) {
        return `  Webhook failed: ${url} ✗ (HTTP ${res.status})`;
      }
      return `  Webhook sent: ${url} ✓`;
    }
    return `  Webhook failed: ${url} ✗ (${result.reason instanceof Error ? result.reason.message : String(result.reason)})`;
  });
};
